import React, { useMemo, useState, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { supabase } from '../../../utils/supabase';
import { regeneratePhotoUrl } from '../../../services/checkInService';

// Constants
const DAYS_IN_WEEK = 7;
const MINUTES_IN_DAY = 1440;
const HOURS_IN_DAY = 24;
const MS_IN_DAY = 24 * 60 * 60 * 1000;

const STATUS_COLORS = {
  approved: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  default: 'bg-gray-100 text-gray-800 border-gray-200'
};

const STATUS_BAR_COLORS = {
  approved: 'bg-green-500',
  pending: 'bg-yellow-500',
  rejected: 'bg-red-500'
};

const STATUS_ENTRY_COLORS = {
  approved: 'border-green-500 bg-green-50',
  pending: 'border-yellow-500 bg-yellow-50',
  rejected: 'border-red-500 bg-red-50'
};

const TimesheetCalendarView = ({ entries, contracts, dateRange, onEntryClick }) => {
  // State management
  const [selectedDay, setSelectedDay] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [checkInPhotos, setCheckInPhotos] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [loadingImages, setLoadingImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  const [refreshingUrls, setRefreshingUrls] = useState(new Set());
  
  // Utility functions
  const getDateString = useCallback((date) => date.toISOString().split('T')[0], []);

  // Get last 30 days as default
  const getDefaultMonthRange = useCallback(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
    return {
      start: getDateString(thirtyDaysAgo),
      end: getDateString(today)
    };
  }, [getDateString]);

  // Use provided dateRange or default to current month
  const effectiveDateRange = dateRange?.start && dateRange?.end ? dateRange : getDefaultMonthRange();

  // Generate calendar grid based on date range
  const calendarData = useMemo(() => {
    if (!effectiveDateRange.start || !effectiveDateRange.end) {
      return { weeks: [], month: '', year: '' };
    }

    // Parse dates without timezone issues
    const [startYear, startMonth, startDay] = effectiveDateRange.start.split('-').map(Number);
    const [endYear, endMonth, endDay] = effectiveDateRange.end.split('-').map(Number);
    
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const today = getDateString(new Date());
    
    // Find the first Sunday on or before the start date
    const calendarStart = new Date(start);
    calendarStart.setDate(calendarStart.getDate() - calendarStart.getDay());
    
    // Find the last Saturday on or after the end date
    const calendarEnd = new Date(end);
    calendarEnd.setDate(calendarEnd.getDate() + (6 - calendarEnd.getDay()));
    
    // Generate weeks
    const weeks = [];
    let currentWeek = [];
    const currentDate = new Date(calendarStart);
    
    while (currentDate <= calendarEnd) {
      const dateStr = getDateString(currentDate);
      const dayEntries = entries.filter(entry => 
        getDateString(new Date(entry.start_time)) === dateStr
      );
      
      currentWeek.push({
        date: new Date(currentDate),
        dateStr,
        entries: dayEntries,
        isInRange: currentDate >= start && currentDate <= end,
        isToday: dateStr === today
      });
      
      if (currentWeek.length === DAYS_IN_WEEK) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      weeks,
      dateRangeLabel: `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    };
  }, [entries, effectiveDateRange, getDateString]);

  // Utility functions
  const getStatusColor = useCallback((status) => STATUS_COLORS[status] || STATUS_COLORS.default, []);

  const getTotalHoursForDay = useCallback((dayEntries) => 
    dayEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours_logged) || 0), 0),
    []
  );

  const formatTime = useCallback((dateString) => 
    new Date(dateString).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }),
    []
  );

  const getTimePosition = useCallback((time) => {
    const date = new Date(time);
    const totalMinutes = date.getHours() * 60 + date.getMinutes();
    return (totalMinutes / MINUTES_IN_DAY) * 100;
  }, []);

  // Event handlers
  const handleDayClick = useCallback((day) => {
    if (day.entries.length > 0) {
      setSelectedDay(day);
      fetchCheckInPhotos(day.entries);
    }
  }, []);

  const closeTimeline = useCallback(() => {
    setSelectedDay(null);
    setCheckInPhotos({});
  }, []);

  // Photo fetching logic
  const fetchCheckInPhotos = useCallback(async (dayEntries) => {
    setLoadingPhotos(true);
    setLoadingImages(new Set());
    setFailedImages(new Set());
    
    try {
      const photos = {};
      const loadingSet = new Set();
      
      await Promise.all(
        dayEntries.map(async (entry) => {
          try {
            let photoData = null;

            // Method 1: Try fetching via shift_id if available
            if (entry.shift_id) {
              const { data, error } = await supabase
                .from('va_checkins')
                .select('photo_url, photo_storage_path')
                .eq('shift_id', entry.shift_id)
                .order('check_in_time', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (!error && data) {
                photoData = data;
              }
            }

            // Method 2: Fallback - try fetching by contract_id and date range
            if (!photoData && entry.contract_id) {
              const entryDate = new Date(entry.start_time);
              const dayStart = new Date(
                entryDate.getFullYear(),
                entryDate.getMonth(),
                entryDate.getDate()
              );
              const dayEnd = new Date(dayStart.getTime() + MS_IN_DAY);

              const { data, error } = await supabase
                .from('va_checkins')
                .select('photo_url, photo_storage_path')
                .eq('contract_id', entry.contract_id)
                .gte('check_in_time', dayStart.toISOString())
                .lt('check_in_time', dayEnd.toISOString())
                .order('check_in_time', { ascending: false })
                .limit(1)
                .maybeSingle();

              if (!error && data) {
                photoData = data;
              }
            }

            if (photoData?.photo_url || photoData?.photo_storage_path) {
              photos[entry.id] = photoData;
              loadingSet.add(entry.id);
            }
          } catch (entryError) {
            console.error(`[Calendar] Error fetching photo for entry ${entry.id}:`, entryError);
          }
        })
      );
      
      setCheckInPhotos(photos);
      setLoadingImages(loadingSet);
    } catch (err) {
      console.error('[Calendar] Error fetching check-in photos:', err);
    } finally {
      setLoadingPhotos(false);
    }
  }, []);

  // Image loading handlers
  const handleImageLoad = useCallback((entryId) => {
    setLoadingImages(prev => {
      const next = new Set(prev);
      next.delete(entryId);
      return next;
    });
  }, []);

  const handleImageError = useCallback((entryId) => {
    setLoadingImages(prev => {
      const next = new Set(prev);
      next.delete(entryId);
      return next;
    });
    setFailedImages(prev => new Set(prev).add(entryId));
  }, []);

  const handleRefreshUrl = useCallback(async (entryId, photoStoragePath) => {
    if (!photoStoragePath) return;

    setRefreshingUrls(prev => new Set(prev).add(entryId));
    setFailedImages(prev => {
      const next = new Set(prev);
      next.delete(entryId);
      return next;
    });

    try {
      const result = await regeneratePhotoUrl(photoStoragePath);
      
      if (result.error) {
        throw result.error;
      }

      setCheckInPhotos(prev => ({
        ...prev,
        [entryId]: {
          ...prev[entryId],
          photo_url: result.url
        }
      }));
    } catch (err) {
      console.error('[Calendar] Error refreshing photo URL:', err);
      setFailedImages(prev => new Set(prev).add(entryId));
    } finally {
      setRefreshingUrls(prev => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }
  }, []);

  const renderCheckInPhoto = (entry) => {
    const photoData = checkInPhotos[entry.id];
    if (!photoData) return null;

    const isLoading = loadingImages.has(entry.id);
    const hasFailed = failedImages.has(entry.id);
    const isRefreshing = refreshingUrls.has(entry.id);

    if (!photoData.photo_url && !photoData.photo_storage_path) {
      return (
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Image" size={20} className="text-gray-400" />
        </div>
      );
    }

    if (hasFailed || isRefreshing) {
      return (
        <div className="relative w-16 h-16 bg-gray-100 rounded-lg flex flex-col items-center justify-center gap-1 flex-shrink-0">
          <Icon name="AlertCircle" size={16} className="text-gray-400" />
          {photoData.photo_storage_path && !isRefreshing && (
            <button
              onClick={() => handleRefreshUrl(entry.id, photoData.photo_storage_path)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              title="Regenerate photo URL"
            >
              Refresh
            </button>
          )}
          {isRefreshing && (
            <Icon name="Loader" size={12} className="text-gray-400 animate-spin" />
          )}
        </div>
      );
    }

    return (
      <div className="relative w-16 h-16 flex-shrink-0">
        {isLoading && (
          <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
            <Icon name="Loader" size={16} className="text-gray-400 animate-spin" />
          </div>
        )}
        <button
          onClick={() => setSelectedImage(photoData.photo_url)}
          className="group relative w-full h-full block overflow-hidden rounded-lg border-2 border-gray-200 hover:border-primary-400 transition-all"
          title="Click to view full photo"
        >
          <img
            src={photoData.photo_url}
            alt="Check-in photo"
            className={`w-full h-full object-cover group-hover:opacity-75 transition-opacity ${
              isLoading ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={() => handleImageLoad(entry.id)}
            onError={() => handleImageError(entry.id)}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all pointer-events-none">
            <Icon 
              name="ZoomIn" 
              size={14} 
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </button>
      </div>
    );
  };

  return (
    <div className="calendar-view">
      {/* Calendar Header */}
      <div className="mb-4 text-center">
        <h4 className="text-xl font-semibold text-gray-900">
          {calendarData.dateRangeLabel}
        </h4>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="space-y-2">
        {calendarData.weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-2">
            {week.map((day, dayIndex) => {
              const totalHours = getTotalHoursForDay(day.entries);
              const hasEntries = day.entries.length > 0;
              
              return (
                <div
                  key={dayIndex}
                  onClick={() => handleDayClick(day)}
                  className={`
                    min-h-[120px] border rounded-lg p-2 transition-all
                    ${day.isInRange ? 'bg-white' : 'bg-gray-50'}
                    ${day.isToday ? 'ring-2 ring-primary-500' : ''}
                    ${hasEntries ? 'hover:shadow-md hover:ring-2 hover:ring-primary-300 cursor-pointer' : ''}
                  `}
                >
                  {/* Date Number */}
                  <div className={`
                    text-sm font-medium mb-1
                    ${day.isInRange ? 'text-gray-900' : 'text-gray-400'}
                    ${day.isToday ? 'text-primary-600 font-bold' : ''}
                  `}>
                    {day.date.getDate()}
                  </div>

                  {/* Entries for this day */}
                  {hasEntries && (
                    <div className="space-y-1">
                      {/* Summary badge */}
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700">
                          {totalHours.toFixed(1)}h
                        </span>
                        <span className="text-gray-500">
                          {day.entries.length} {day.entries.length === 1 ? 'entry' : 'entries'}
                        </span>
                      </div>

                      {/* Entry indicators */}
                      <div className="space-y-1">
                        {day.entries.slice(0, 3).map((entry) => {
                          const contract = contracts.find(c => c.id === entry.contract_id);
                          return (
                            <div
                              key={entry.id}
                              onClick={() => onEntryClick && onEntryClick(entry)}
                              className={`
                                text-xs px-2 py-1 rounded border
                                hover:shadow-sm transition-shadow
                                ${getStatusColor(entry.status)}
                              `}
                              title={`${contract?.employer?.company_name || 'Unknown'} - ${entry.hours_logged}h - ${entry.status}`}
                            >
                              <div className="truncate font-medium">
                                {contract?.employer?.company_name || 'Unknown'}
                              </div>
                              <div className="flex items-center justify-between">
                                <span>{parseFloat(entry.hours_logged).toFixed(1)}h</span>
                                {entry.status === 'approved' && (
                                  <Icon name="CheckCircle" size={12} />
                                )}
                                {entry.status === 'pending' && (
                                  <Icon name="Clock" size={12} />
                                )}
                                {entry.status === 'rejected' && (
                                  <Icon name="XCircle" size={12} />
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* More entries indicator */}
                        {day.entries.length > 3 && (
                          <div className="text-xs text-gray-500 text-center py-1">
                            +{day.entries.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-100 border border-green-200"></div>
          <span className="text-gray-600">Approved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200"></div>
          <span className="text-gray-600">Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
          <span className="text-gray-600">Rejected</span>
        </div>
      </div>

      {/* Timeline Modal */}
      {selectedDay && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeTimeline}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Timeline - {selectedDay.date.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedDay.entries.length} {selectedDay.entries.length === 1 ? 'entry' : 'entries'} • {getTotalHoursForDay(selectedDay.entries).toFixed(2)} hours total
                </p>
              </div>
              <button
                onClick={closeTimeline}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Modal Body - Timeline View */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Timeline Container */}
              <div className="relative">
                {/* Time Labels */}
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                  <span>12:00 AM</span>
                  <span>6:00 AM</span>
                  <span>12:00 PM</span>
                  <span>6:00 PM</span>
                  <span>11:59 PM</span>
                </div>

                {/* Timeline Bar */}
                <div className="relative h-2 bg-gray-100 rounded-full mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-yellow-500/20 to-blue-500/20 rounded-full"></div>
                </div>

                {/* Entries List */}
                <div className="space-y-4">
                  {selectedDay.entries
                    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                    .map((entry) => {
                      const contract = contracts.find(c => c.id === entry.contract_id);
                      const startTime = new Date(entry.start_time);
                      const endTime = entry.end_time ? new Date(entry.end_time) : null;
                      const duration = parseFloat(entry.hours_logged);

                      return (
                        <div
                          key={entry.id}
                          className={`border-l-4 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${
                            STATUS_ENTRY_COLORS[entry.status] || ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 mr-4">
                              {/* Company/Employer */}
                              <div className="flex items-center gap-2 mb-2">
                                <Icon 
                                  name="Briefcase" 
                                  size={16} 
                                  className="text-gray-600"
                                />
                                <h4 className="font-semibold text-gray-900">
                                  {contract?.employer?.company_name || contract?.employer?.full_name || 'Unknown Employer'}
                                </h4>
                              </div>

                              {/* Project Name */}
                              {entry.project?.name && (
                                <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                                  <Icon name="Folder" size={14} />
                                  <span>{entry.project.name}</span>
                                </div>
                              )}

                              {/* Time Range */}
                              <div className="flex items-center gap-2 mb-2 text-sm">
                                <Icon name="Clock" size={14} className="text-gray-500" />
                                <span className="font-medium text-gray-700">
                                  {formatTime(entry.start_time)}
                                  {endTime && ` - ${formatTime(entry.end_time)}`}
                                </span>
                                <span className="text-gray-500">
                                  ({duration.toFixed(2)} {duration === 1 ? 'hour' : 'hours'})
                                </span>
                              </div>

                              {/* Description */}
                              {entry.description && (
                                <div className="mt-2 text-sm text-gray-600 bg-white rounded p-2">
                                  <Icon name="FileText" size={14} className="inline mr-1" />
                                  {entry.description}
                                </div>
                              )}

                              {/* Status Badge */}
                              <div className="mt-3 flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  STATUS_COLORS[entry.status] || STATUS_COLORS.default
                                }`}>
                                  {entry.status === 'approved' && <Icon name="CheckCircle" size={12} />}
                                  {entry.status === 'pending' && <Icon name="Clock" size={12} />}
                                  {entry.status === 'rejected' && <Icon name="XCircle" size={12} />}
                                  {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                </span>

                                {/* Earning */}
                                {contract?.hourly_rate && (
                                  <span className="text-sm text-gray-600">
                                    ${(duration * parseFloat(contract.hourly_rate)).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Check-in Photo Thumbnail - positioned on right */}
                            <div className="flex-shrink-0 ml-auto">
                              {renderCheckInPhoto(entry)}
                            </div>
                          </div>

                          {/* Visual Timeline Bar for this entry */}
                          <div className="mt-3 relative h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`absolute h-full rounded-full ${
                                STATUS_BAR_COLORS[entry.status] || ''
                              }`}
                              style={{
                                left: `${getTimePosition(entry.start_time)}%`,
                                width: `${(duration / HOURS_IN_DAY) * 100}%`
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={closeTimeline}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <div 
            className="relative bg-white rounded-lg shadow-2xl max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>

            {/* Image */}
            <img
              src={zoomedImage}
              alt="Check-in photo zoom"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Lightbox Modal for selected image */}
      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          title="Check-in Photo"
          size="lg"
        >
          <div className="flex items-center justify-center p-4">
            <img
              src={selectedImage}
              alt="Full size check-in"
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TimesheetCalendarView;
